import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    inject,
    Input,
    OnInit,
    Optional,
    Inject,
    ViewChild,
    ElementRef,
    Output,
    EventEmitter
} from '@angular/core';
import { ApiClientService, CachedValuesService, ToastService } from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';
import { ListTypeItem } from 'app/types/list-type-item.type';
import { Observable, map, take } from 'rxjs';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ControlContainer } from '@angular/forms';
import { EditForm } from './listtype-select.model';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
    selector: 'app-listtype-select',
    templateUrl: './listtype-select.component.html',
    styleUrl: './listtype-select.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ListTypeSelectComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListTypeSelectComponent<T> implements ControlValueAccessor, OnInit {
    @ViewChild('nameInput') public nameInputRef: ElementRef<HTMLInputElement>;

    @Input() public listType: string;
    @Input() public showPotentialValidationMessages: boolean;
    @Input() public formControlName: string;
    @Input() public highlightAnimated: boolean = false;
    @Input() public highlightFocused: boolean = false;
    @Output() public highlightFocus = new EventEmitter<void>();
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly cachedValuesService = inject(CachedValuesService);

    private controlContainer: ControlContainer;
    public constructor(@Optional() @Inject(ControlContainer) controlContainer: ControlContainer) {
        this.controlContainer = controlContainer;
    }

    public editMode: boolean;
    public items$: Observable<DropdownItem<string>[]>;

    public editForm: FormGroup<EditForm>;

    public get f() {
        return this.editForm.controls;
    }

    public get disabled() {
        return this.control.disabled;
    }

    public get canStore() {
        return this.f.name.valid && this.f.name.dirty;
    }

    public get control(): FormControl {
        if (this.formControlName && this.controlContainer) {
            return this.controlContainer.control.get(this.formControlName) as FormControl;
        }
        return null;
    }

    public ngOnInit(): void {
        this.editForm = new FormGroup({
            name: new FormControl()
        });
        this.fetchData();
    }

    public setEditMode() {
        this.editMode = !this.editMode;
        if (this.editMode) {
            this.f.name.setValue('');
            setTimeout(() => {
                this.nameInputRef?.nativeElement.focus();
            });
        }
        this.changeDetectorRef.markForCheck();
    }

    private fetchData() {
        this.items$ = this.apiClientService.getTypeListItems$(this.listType).pipe(
            map((data) =>
                data.map((item: ListTypeItem) => {
                    return {
                        value: item.name,
                        label: item.value
                    } as DropdownItem<string>;
                })
            )
        );
    }

    public create() {
        const request = {
            name: this.f.name.value
        };
        this.apiClientService
            .postListTypeItem$(this.listType, request)
            .pipe(untilDestroyed(this), take(1))
            .subscribe({
                next: () => {
                    this.editMode = false;
                    this.fetchData();
                    this.control.setValue(this.f.name.value);
                    this.cachedValuesService.invalidateCache();
                    this.control.markAsDirty();
                    this.changeDetectorRef.markForCheck();
                },
                error: () => {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant('TOAST_MESSAGES.CREATE_LISTITEM_ERROR') as string;
                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }

    public value: T;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public onChange = (_value: T) => {
        /* Intentionally left blank for ControlValueAccessor */
    };
    public onTouched = () => {
        /* Intentionally left blank for ControlValueAccessor */
    };

    public writeValue(value: T): void {
        this.value = value;
    }
    public registerOnChange(fn: (value: T) => T) {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void) {
        this.onTouched = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    public setDisabledState(isDisabled: boolean): void {}

    public onDropdownChange(value: T) {
        this.value = value;
        this.onChange(value);
        this.onTouched();
    }

    public onHighlightFocus() {
        this.highlightFocus.emit();
    }
}
